import { Box, Typography } from "@mui/material";
import { Tag } from "Components/Analyses/_Shared/Tag";
import { Flex } from "Components/_Shared/Core/Flex";
import { HStack } from "Components/_Shared/Core/Stack";
import { Dot } from "Components/_Shared/Dot";
import { ReactComponent as GitBranchIcon } from "Components/_Shared/Icons/Assets/git-branch.svg";
import { UserInitialsAvatar } from "Components/_Shared/UserInitialsAvatar";
import type { ClientAnalysisReference, Detail } from "Infrastructure/ApiZfp/Schema";
import { useDateFnsLocale } from "Infrastructure/Hooks/DateFns/useDateFnsLocale";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { format } from "date-fns";
import { AnalysisStatusLabel } from "../_Shared/AnalysisStatusLabel";
import { ContinueExistingAnalysisButton } from "./ContinueExistingAnalysisButton";

type Props = {
  readonly analysis: ClientAnalysisReference;
  readonly advisor?: Detail;
  readonly isContinueDisabled?: boolean;
};

export const ExistingAnalysis: React.FC<Props> = ({ analysis, advisor, isContinueDisabled = false }) => {
  const locale = useDateFnsLocale();
  const { t } = useResource();

  return (
    <Box
      p={Spacing.L}
      sx={(theme) => ({
        border: `1px solid ${theme.colors.borderLight}`,
        borderRadius: theme.spacing(Radius.M),
      })}
    >
      <Flex justifyContent="space-between" alignItems="center" gap={Spacing.M}>
        <Typography variant="body2" fontWeight={600}>
          {analysis.name}
        </Typography>
        <Tag
          variant="solid"
          size="m"
          icon={
            <Flex color="text.disabled">
              <GitBranchIcon />
            </Flex>
          }
          title={t(Resources.NewAnalysis.ExistingAnalysis.Version, { version: analysis.version })}
        />
      </Flex>

      <Typography variant="caption" color="text.secondary" mt={Spacing.XS}>
        {t(Resources.NewAnalysis.ExistingAnalysis.Created, {
          date: format(new Date(analysis.createdAt), "d. M. yyyy", { locale }),
        })}
      </Typography>

      {/* Another advisor's analysis is read-only, and says whose it is */}
      {!analysis.isOwner && advisor && (
        <HStack spacing={Spacing.M} alignItems="center" mt={Spacing.XS}>
          <Typography variant="caption" color="text.secondary">
            {t(Resources.NewAnalysis.ExistingAnalysis.Creator)}
          </Typography>
          <UserInitialsAvatar firstName={advisor.firstName} surname={advisor.lastName} fullName={advisor.name} variant="xs" />
          {/* A falsy child is dropped by MUI before the dividers are inserted, so a missing e-mail leaves no dangling dot */}
          <HStack spacing={Spacing.M} alignItems="center" flexWrap="wrap" divider={<Dot />}>
            <Typography variant="caption" fontWeight={600}>
              {advisor.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {advisor.ssoId}
            </Typography>
            {advisor.email && (
              <Typography variant="caption" color="text.secondary">
                {advisor.email}
              </Typography>
            )}
          </HStack>
        </HStack>
      )}

      <Flex justifyContent="space-between" alignItems="center" mt={Spacing.M}>
        <AnalysisStatusLabel state={analysis.state} coloredText />
        {analysis.isOwner && (
          <ContinueExistingAnalysisButton
            analysisId={analysis.analysisId}
            versionId={analysis.versionId}
            analysisState={analysis.state}
            isDisabled={isContinueDisabled}
          />
        )}
      </Flex>
    </Box>
  );
};
